import type { ReactNode } from 'react';
import type { WorkflowVersionSummary } from '@erp/rpc';
import { useGT } from 'gt-react';
import { HistoryGraph } from '@/components/common/HistoryGraph';
import { lineageGraph } from '@/lib/undoTree';
import { cn } from '@/lib/utils';

const ROW_HEIGHT = 48;
const LANE_WIDTH = 12;

export function VersionLineageGraph({
  versions,
  currentlyViewingId,
  renderVersion,
}: {
  versions: WorkflowVersionSummary[];
  currentlyViewingId?: string;
  renderVersion: (
    version: WorkflowVersionSummary,
    isViewing: boolean,
  ) => ReactNode;
}) {
  const gt = useGT();
  const graph = lineageGraph(
    versions.map((version) => ({
      ...version,
      parentId: version.parentVersionId ?? null,
      order: version.version,
    })),
    { currentId: currentlyViewingId },
  );
  return (
    <HistoryGraph
      graph={graph}
      rowHeight={ROW_HEIGHT}
      laneWidth={LANE_WIDTH}
      rowKey={(row) => row.node.id}
      label={gt('Version lineage', {
        context: 'workflow version provenance graph',
      })}
      renderRow={(row, cell) => (
        <div
          data-version-id={row.node.id}
          data-active={row.isActive}
          aria-current={row.isCurrent ? 'step' : undefined}
          className={cn(
            'relative flex gap-2 px-2',
            row.isCurrent && 'bg-muted/40',
          )}
        >
          <div
            className="relative shrink-0 self-stretch"
            style={{ width: graph.laneCount * LANE_WIDTH }}
          >
            {cell}
            {/* Continue lanes through expanded changelogs without stretching the dots. */}
            <svg
              aria-hidden
              className="absolute bottom-0 w-full"
              style={{
                top: ROW_HEIGHT,
                height: `calc(100% - ${ROW_HEIGHT}px)`,
              }}
              fill="none"
              strokeWidth={1.5}
            >
              {[
                ...row.through,
                ...(row.hasParent
                  ? [{ fromLane: row.lane, isActive: row.isActive }]
                  : []),
              ].map((line) => (
                <line
                  key={line.fromLane}
                  x1={LANE_WIDTH / 2 + line.fromLane * LANE_WIDTH}
                  x2={LANE_WIDTH / 2 + line.fromLane * LANE_WIDTH}
                  y1={0}
                  y2="100%"
                  className={
                    line.isActive
                      ? 'stroke-primary'
                      : 'stroke-muted-foreground/40'
                  }
                />
              ))}
            </svg>
          </div>
          {renderVersion(row.node, row.isCurrent)}
        </div>
      )}
    />
  );
}
